import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import {
  FONT_FAMILY,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
} from "../../utils/constants";
import {
  GREEN_COLOR,
  GREY_DISABLED_COLOR,
  INPUT_COLOR_BORDER,
  LINEAR_GRADIENT,
  PRIMARY_COLOR,
  RED_COLOR,
} from "../../utils/colors";
import i18next from "i18next";
import {
  BUTTON_GO_BACK,
  BUTTON_YES_I_SURE,
  OK,
} from "../../i18n/keysTranslations";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

const MySwal = withReactContent(Swal);
const minWidthButton = "150px";
const SWEETALERT_Z_INDEX = 9999;
const ALERT_STYLE_TAG_ID = "bz-swal-alert-styles";
const MUI_ICON_CLASS = "bz-alert__icon--mui";
const muiIconRootsByPopup = new WeakMap();
const WARNING_YELLOW_ACCENT = "#FFBB28";

const BASE_CUSTOM_CLASS = Object.freeze({
  container: "bz-alert-container",
  popup: "bz-alert-popup",
  title: "bz-alert__title",
  htmlContainer: "bz-alert__body",
  actions: "bz-alert__actions",
  confirmButton: "bz-alert__button bz-alert__button--primary",
  cancelButton: "bz-alert__button bz-alert__button--secondary",
  denyButton: "bz-alert__button bz-alert__button--primary",
  icon: "bz-alert__icon",
});

const BUTTON_VARIANT = Object.freeze({
  PRIMARY: "primary",
  SECONDARY: "secondary",
});

const ALERT_DEFAULT_BUTTON_VARIANTS = Object.freeze({
  ConfirmAlert: {
    confirm: BUTTON_VARIANT.PRIMARY,
    cancel: BUTTON_VARIANT.SECONDARY,
    deny: BUTTON_VARIANT.PRIMARY,
  },
  ConfirmUsageAlert: {
    confirm: BUTTON_VARIANT.PRIMARY,
    cancel: BUTTON_VARIANT.SECONDARY,
    deny: BUTTON_VARIANT.PRIMARY,
  },
  SimpleAlert: {
    confirm: BUTTON_VARIANT.PRIMARY,
  },
});

const getValidButtonVariant = (variant, fallbackVariant) => {
  if (
    variant === BUTTON_VARIANT.PRIMARY ||
    variant === BUTTON_VARIANT.SECONDARY
  ) {
    return variant;
  }
  return fallbackVariant;
};

const resolveButtonVariants = ({
  alertType,
  confirmButtonVariant,
  cancelButtonVariant,
  denyButtonVariant,
}) => {
  const defaults = ALERT_DEFAULT_BUTTON_VARIANTS[alertType] || {};
  const confirm = getValidButtonVariant(confirmButtonVariant, defaults.confirm);
  const cancel = getValidButtonVariant(cancelButtonVariant, defaults.cancel);
  const deny = getValidButtonVariant(denyButtonVariant, defaults.deny);

  return { confirm, cancel, deny };
};

const getStatusAccent = (icon) => {
  if (icon === "success") return GREEN_COLOR;
  if (icon === "error") return RED_COLOR;
  if (icon === "warning") return WARNING_YELLOW_ACCENT;
  return PRIMARY_COLOR;
};

const getStatusGradient = (icon) => {
  if (icon === "success") return LINEAR_GRADIENT[0];
  if (icon === "warning") return LINEAR_GRADIENT[1];
  if (icon === "error") return LINEAR_GRADIENT[1];
  if (icon === "info") return LINEAR_GRADIENT[2];
  return LINEAR_GRADIENT[2];
};

const hexToRgba = (hex, alpha) => {
  if (typeof hex !== "string") return `rgba(0,0,0,${alpha})`;
  const raw = hex.replace("#", "").trim();
  if (raw.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return `rgba(0,0,0,${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ensureAlertsStylesInjected = () => {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(ALERT_STYLE_TAG_ID);
  if (existing) return;

  const style = document.createElement("style");
  style.id = ALERT_STYLE_TAG_ID;
  style.textContent = `
    @keyframes bzAlertIn {
      0% { opacity: 0; transform: translateY(8px) scale(.985); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }

    .swal2-container.bz-alert-container {
      z-index: ${SWEETALERT_Z_INDEX} !important;
    }

    .swal2-popup.bz-alert-popup:not(.swal2-hide) {
      animation: bzAlertIn 220ms cubic-bezier(.2,.9,.2,1) both;
      border-radius: 22px;
      padding: 18px 18px 16px;
      background: var(--bz-alert-bg, #fff);
      color: var(--bz-alert-fg, #111);
      border: 1px solid var(--bz-alert-border, ${INPUT_COLOR_BORDER});
      box-shadow: 0 28px 70px rgba(0,0,0,.18);
      overflow: hidden;
    }

    .swal2-popup.bz-alert-popup::before {
      content: "";
      position: absolute;
      inset: -2px -2px auto -2px;
      height: 110px;
      background: radial-gradient(120px 90px at 36px 42px, var(--bz-alert-accent-soft, rgba(0,0,0,.08)), rgba(0,0,0,0) 70%);
      pointer-events: none;
    }

    .swal2-title.bz-alert__title {
      padding: 6px 14px 0;
      margin: 0;
      color: var(--bz-alert-fg, #111);
      line-height: 1.15;
      font-size: 25px;
      font-weight: 720;
      letter-spacing: -0.015em;
    }

    .swal2-html-container.bz-alert__body {
      margin: 12px 14px 4px;
      color: var(--bz-alert-fg, #111);
      line-height: 1.35;
      font-size: 15px;
      opacity: .92;
    }

    .swal2-actions.bz-alert__actions {
      margin: 14px 8px 4px;
      gap: 10px;
      align-items: stretch;
    }

    .swal2-styled.bz-alert__button {
      border-radius: 999px !important;
      min-width: ${minWidthButton};
      font-family: ${FONT_FAMILY};
      font-size: ${FONT_SIZE_BUTTON}px;
      font-weight: ${FONT_WEIGHT_BUTTON};
      padding: 12px 16px;
      line-height: 1;
      letter-spacing: .01em;
      transition: transform 120ms ease, box-shadow 160ms ease, background-color 160ms ease, color 160ms ease, border-color 160ms ease;
      box-shadow: 0 10px 22px rgba(0,0,0,.14);
    }

    .swal2-styled.bz-alert__button:focus,
    .swal2-styled.bz-alert__button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(100, 150, 200, .5), 0 10px 22px rgba(0,0,0,.14) !important;
    }

    .swal2-styled.bz-alert__button:active {
      transform: translateY(1px) scale(.99);
    }

    .swal2-styled.bz-alert__button--primary {
      background: var(--bz-btn-primary-bg, #111) !important;
      color: var(--bz-btn-primary-fg, #fff) !important;
      border: 1px solid transparent !important;
    }

    .swal2-styled.bz-alert__button--primary:hover {
      background: var(--bz-btn-primary-bg-hover, #000) !important;
      color: var(--bz-btn-primary-fg-hover, #fff) !important;
    }

    .swal2-styled.bz-alert__button--disabled,
    .swal2-styled.bz-alert__button:disabled {
      opacity: 0.45 !important;
      cursor: not-allowed !important;
      pointer-events: none !important;
      transform: none !important;
      box-shadow: none !important;
      background: ${GREY_DISABLED_COLOR} !important;
      color: #fff !important;
      border-color: transparent !important;
    }

    .swal2-styled.bz-alert__button--secondary {
      background: var(--bz-btn-secondary-bg, #fff) !important;
      color: var(--bz-btn-secondary-fg, #111) !important;
      border: 1px solid var(--bz-btn-secondary-border, ${INPUT_COLOR_BORDER}) !important;
      box-shadow: 0 10px 22px rgba(0,0,0,.10);
    }

    .swal2-styled.bz-alert__button--secondary:hover {
      background: var(--bz-btn-secondary-bg-hover, #f5f5f5) !important;
      color: var(--bz-btn-secondary-fg-hover, #111) !important;
    }

    .swal2-icon.bz-alert__icon {
      width: 5em;
      height: 5em;
      margin: 14px auto 0;
      margin-bottom: 6px;
      font-size: clamp(12px, 2.6vw, 14px);
      border-color: var(--bz-alert-accent, ${PRIMARY_COLOR}) !important;
      color: var(--bz-alert-accent, ${PRIMARY_COLOR}) !important;
      background: transparent !important;
      box-shadow: none !important;
      filter: none !important;
    }

    .swal2-icon.bz-alert__icon.${MUI_ICON_CLASS} {
      border: none !important;
      background: transparent !important;
      background-image: var(
        --bz-alert-icon-gradient,
        linear-gradient(
          to bottom right,
          rgba(34, 197, 94, 0.9),
          rgba(5, 150, 105, 0.9)
        )
      ) !important;
      background-size: cover;
      background-position: center;
      border-radius: 999px !important;
      width: 60px !important;
      height: 60px !important;
      display: grid;
      place-items: center;
      overflow: hidden;
      color: var(--bz-alert-icon-fg, #fff) !important;
      animation: none !important;
      transition: none !important;
    }

    .swal2-icon.bz-alert__icon.${MUI_ICON_CLASS} svg {
      animation: none !important;
      transition: none !important;
    }

    .swal2-icon.bz-alert__icon .swal2-x-mark-line-left,
    .swal2-icon.bz-alert__icon .swal2-x-mark-line-right {
      background-color: var(--bz-alert-accent, ${PRIMARY_COLOR}) !important;
    }

    .swal2-icon.bz-alert__icon .swal2-success-line-tip,
    .swal2-icon.bz-alert__icon .swal2-success-line-long {
      background-color: var(--bz-alert-accent, ${PRIMARY_COLOR}) !important;
    }

    .swal2-icon.bz-alert__icon .swal2-success-ring {
      border-color: var(--bz-alert-accent, ${PRIMARY_COLOR}) !important;
    }

    .swal2-icon.bz-alert__icon .swal2-success-fix,
    .swal2-icon.bz-alert__icon .swal2-success-circular-line-left,
    .swal2-icon.bz-alert__icon .swal2-success-circular-line-right {
      background-color: var(--bz-alert-bg, #fff) !important;
    }
  `;

  document.head.appendChild(style);
};

const getMuiIconForSwalIcon = (icon) => {
  if (icon === "success") return CheckCircleRoundedIcon;
  if (icon === "error") return ErrorRoundedIcon;
  if (icon === "warning") return InfoRoundedIcon;
  if (icon === "info") return InfoRoundedIcon;
  return InfoRoundedIcon;
};

const unmountMuiIconFromPopup = (popup) => {
  if (!popup) return;
  const existingRoot = muiIconRootsByPopup.get(popup);
  if (!existingRoot) return;
  existingRoot.unmount();
  muiIconRootsByPopup.delete(popup);
};

const mountMuiIconIntoPopup = ({ popup, icon }) => {
  if (!popup) return;
  if (!icon) return;

  const iconEl =
    (typeof MySwal.getIcon === "function" && MySwal.getIcon()) ||
    popup.querySelector(".swal2-icon");
  if (!iconEl) return;

  unmountMuiIconFromPopup(popup);

  iconEl.classList.add(MUI_ICON_CLASS);
  iconEl.setAttribute("data-bz-alert-icon", icon);
  iconEl.innerHTML = "";

  const RootIcon = getMuiIconForSwalIcon(icon);
  const root = createRoot(iconEl);
  root.render(
    createElement(RootIcon, {
      sx: {
        fontSize: 40,
        color: "var(--bz-alert-icon-fg, #fff)",
      },
    })
  );

  muiIconRootsByPopup.set(popup, root);
};

const appendAdditionalText = (additionalText) => {
  if (!additionalText) return;
  const htmlContainer = MySwal.getHtmlContainer();
  if (!htmlContainer) return;

  htmlContainer.appendChild(document.createElement("br"));
  htmlContainer.appendChild(document.createElement("br"));
  htmlContainer.appendChild(document.createTextNode(additionalText));
};

const setPopupThemeVars = (popup, { icon }) => {
  if (!popup || typeof window === "undefined") return;
  const accent = getStatusAccent(icon);
  popup.style.setProperty("--bz-alert-bg", window.paper || "#fff");
  popup.style.setProperty(
    "--bz-alert-fg",
    window.paperSecondaryContrastText || "#111"
  );
  popup.style.setProperty("--bz-alert-border", INPUT_COLOR_BORDER);
  popup.style.setProperty("--bz-alert-accent", accent);
  popup.style.setProperty("--bz-alert-accent-soft", hexToRgba(accent, 0.16));
  popup.style.setProperty("--bz-alert-accent-ring", hexToRgba(accent, 0.22));
  popup.style.setProperty("--bz-alert-icon-gradient", getStatusGradient(icon));
  popup.style.setProperty("--bz-alert-icon-fg", "#fff");

  popup.style.setProperty(
    "--bz-btn-primary-bg",
    window.buttonPrimaryColor || "#111"
  );
  popup.style.setProperty(
    "--bz-btn-primary-fg",
    window.buttonPrimaryContrastText || "#fff"
  );
  popup.style.setProperty(
    "--bz-btn-primary-bg-hover",
    window.buttonPrimaryHover || "#000"
  );
  popup.style.setProperty(
    "--bz-btn-primary-fg-hover",
    window.buttonPrimaryHoverContrastText || "#fff"
  );

  popup.style.setProperty(
    "--bz-btn-secondary-bg",
    window.buttonSecondaryColor || "#fff"
  );
  popup.style.setProperty(
    "--bz-btn-secondary-fg",
    window.buttonSecondaryContrastText || "#111"
  );
  popup.style.setProperty("--bz-btn-secondary-border", INPUT_COLOR_BORDER);
  popup.style.setProperty(
    "--bz-btn-secondary-bg-hover",
    window.buttonSecondaryHover || window.buttonSecondaryColor || "#f5f5f5"
  );
  popup.style.setProperty(
    "--bz-btn-secondary-fg-hover",
    window.buttonSecondaryHoverContrastText ||
      window.buttonSecondaryContrastText ||
      "#111"
  );
};

const applyModalSizing = (popup, width) => {
  if (!popup) return;
  popup.style.backgroundColor = window.paper;
  if (width) {
    popup.style.width = "100%";
    popup.style.maxWidth = width;
  }
};

const applyActionsLayout = (actions, { reverseButtons }) => {
  if (!actions) return;
  actions.style.flexDirection = reverseButtons ? "row" : "row-reverse";
};

const setButtonVariant = (btn, variant) => {
  if (!btn) return;
  if (
    variant !== BUTTON_VARIANT.PRIMARY &&
    variant !== BUTTON_VARIANT.SECONDARY
  ) {
    return;
  }
  btn.classList.remove(
    "bz-alert__button--primary",
    "bz-alert__button--secondary"
  );
  btn.classList.add(`bz-alert__button--${variant}`);
};

const applyButtonsVariant = ({ confirm, cancel, deny }) => {
  const confirmButton = MySwal.getConfirmButton();
  const cancelButton = MySwal.getCancelButton();
  const denyButton = MySwal.getDenyButton();
  setButtonVariant(confirmButton, confirm);
  setButtonVariant(cancelButton, cancel);
  setButtonVariant(denyButton, deny);
};

const applyCompactButtonsIfNeeded = ({ compact }) => {
  if (!compact) return;
  const confirmButton = MySwal.getConfirmButton();
  const cancelButton = MySwal.getCancelButton();
  const denyButton = MySwal.getDenyButton();
  [confirmButton, cancelButton, denyButton].forEach((btn) => {
    if (!btn) return;
    btn.style.minWidth = "120px";
  });
};

export const setSwalConfirmButtonDisabled = (disabled) => {
  const confirmButton = MySwal.getConfirmButton();
  if (!confirmButton) return;

  confirmButton.disabled = disabled;
  confirmButton.setAttribute("aria-disabled", disabled ? "true" : "false");
  confirmButton.classList.toggle("bz-alert__button--disabled", disabled);
};

const stopSwalKeydownCapture = (event) => {
  event.stopPropagation();
};

const enableInteractiveHtmlInSwal = () => {
  const attachInteractiveListeners = () => {
    const htmlContainer = MySwal.getHtmlContainer();
    if (!htmlContainer) {
      return;
    }

    htmlContainer
      .querySelectorAll("input, textarea, select, [contenteditable='true']")
      .forEach((element) => {
        element.addEventListener("keydown", stopSwalKeydownCapture, true);
        element.addEventListener("keyup", stopSwalKeydownCapture, true);
      });
  };

  requestAnimationFrame(attachInteractiveListeners);
};

const didOpenBase = ({
  icon,
  width,
  reverseButtons,
  buttonVariants,
  compactButtons,
  forceActionsRowReverse = false,
  useMuiIcon = true,
}) => {
  ensureAlertsStylesInjected();
  const popup = MySwal.getPopup();
  setPopupThemeVars(popup, { icon });
  if (width) applyModalSizing(popup, width);

  if (useMuiIcon) {
    mountMuiIconIntoPopup({ popup, icon });
  }

  const actions = MySwal.getActions();
  if (forceActionsRowReverse) {
    if (actions) actions.style.flexDirection = "row-reverse";
  } else {
    applyActionsLayout(actions, { reverseButtons });
  }
  applyButtonsVariant(buttonVariants || {});

  applyCompactButtonsIfNeeded({ compact: compactButtons });
};

const buildButtonClass = (variant, fallbackVariant) => {
  const safeVariant = getValidButtonVariant(variant, fallbackVariant);
  return `bz-alert__button bz-alert__button--${safeVariant}`;
};

const buildCustomClass = ({
  withIcon = true,
  includeDeny = true,
  buttonVariants = {},
} = {}) => {
  const customClass = { ...BASE_CUSTOM_CLASS };
  customClass.confirmButton = buildButtonClass(
    buttonVariants.confirm,
    ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmAlert.confirm
  );
  customClass.cancelButton = buildButtonClass(
    buttonVariants.cancel,
    ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmAlert.cancel
  );
  customClass.denyButton = buildButtonClass(
    buttonVariants.deny,
    ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmAlert.deny
  );
  if (!withIcon) delete customClass.icon;
  if (!includeDeny) delete customClass.denyButton;
  return customClass;
};

export const ConfirmAlert = async ({
  title = "",
  text = "",
  additionalText = "",
  confirmButtonText = i18next.t(BUTTON_YES_I_SURE),
  cancelButtonText = i18next.t(BUTTON_GO_BACK),
  showCancelButton = true,
  showThirdButton = false,
  thirdButtonText = "",
  icon = "warning",
  html,
  preConfirm,
  width,
  reverseButtons = false,
  confirmButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmAlert.confirm,
  cancelButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmAlert.cancel,
  denyButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmAlert.deny,
}) => {
  const buttonVariants = resolveButtonVariants({
    alertType: "ConfirmAlert",
    confirmButtonVariant,
    cancelButtonVariant,
    denyButtonVariant,
  });

  return MySwal.fire({
    title,
    text,
    icon,
    showCancelButton,
    confirmButtonText,
    cancelButtonText,
    showDenyButton: showThirdButton,
    denyButtonText: thirdButtonText,
    html,
    preConfirm,
    customClass: buildCustomClass({
      withIcon: true,
      includeDeny: true,
      buttonVariants,
    }),
    didOpen: () => {
      didOpenBase({
        icon,
        width,
        reverseButtons,
        buttonVariants,
        compactButtons: showThirdButton,
      });
      appendAdditionalText(additionalText);
    },
    willClose: (popup) => {
      unmountMuiIconFromPopup(popup);
    },
  });
};
export const ConfirmUsageAlert = async ({
  title = "",
  text = "",
  additionalText = "",
  confirmButtonText = i18next.t(BUTTON_YES_I_SURE),
  cancelButtonText = i18next.t(BUTTON_GO_BACK),
  showCancelButton = true,
  showThirdButton = false,
  thirdButtonText = "",
  html,
  confirmButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmUsageAlert
    .confirm,
  cancelButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmUsageAlert.cancel,
  denyButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.ConfirmUsageAlert.deny,
}) => {
  const buttonVariants = resolveButtonVariants({
    alertType: "ConfirmUsageAlert",
    confirmButtonVariant,
    cancelButtonVariant,
    denyButtonVariant,
  });

  return MySwal.fire({
    title,
    text,
    showCancelButton,
    confirmButtonText,
    cancelButtonText,
    showDenyButton: showThirdButton,
    denyButtonText: thirdButtonText,
    html,
    customClass: buildCustomClass({
      withIcon: false,
      includeDeny: true,
      buttonVariants,
    }),
    didOpen: () => {
      didOpenBase({
        icon: undefined,
        reverseButtons: false,
        buttonVariants,
        compactButtons: showThirdButton,
        forceActionsRowReverse: true,
      });
      appendAdditionalText(additionalText);
    },
    willClose: (popup) => {
      unmountMuiIconFromPopup(popup);
    },
  });
};

export const firePlainHtmlBuzzlyAlert = ({
  html,
  confirmButtonText = i18next.t(OK),
  width = "min(680px, 92vw)",
}) => {
  const buttonVariants = resolveButtonVariants({
    alertType: "SimpleAlert",
    confirmButtonVariant: ALERT_DEFAULT_BUTTON_VARIANTS.SimpleAlert.confirm,
  });

  return MySwal.fire({
    title: "",
    text: "",
    icon: false,
    confirmButtonText,
    html,
    customClass: buildCustomClass({
      withIcon: false,
      includeDeny: false,
      buttonVariants,
    }),
    didOpen: () => {
      didOpenBase({
        icon: false,
        width,
        reverseButtons: false,
        buttonVariants,
        compactButtons: false,
        useMuiIcon: false,
      });
    },
    willClose: (popup) => {
      unmountMuiIconFromPopup(popup);
    },
  });
};

export const SimpleAlert = ({
  title = "",
  text = "",
  additionalText = "",
  icon = "success",
  hasList = false,
  list = [],
  confirmButtonText = "Ok",
  html,
  confirmButtonVariant = ALERT_DEFAULT_BUTTON_VARIANTS.SimpleAlert.confirm,
}) => {
  const buttonVariants = resolveButtonVariants({
    alertType: "SimpleAlert",
    confirmButtonVariant,
  });

  return MySwal.fire({
    title,
    text,
    icon,
    confirmButtonText,
    html,
    stopKeydownPropagation: html ? false : undefined,
    customClass: buildCustomClass({
      withIcon: true,
      includeDeny: false,
      buttonVariants,
    }),
    didOpen: () => {
      didOpenBase({
        icon,
        reverseButtons: false,
        buttonVariants,
        compactButtons: false,
      });
      if (hasList) {
        const htmlContainer = MySwal.getHtmlContainer();
        if (!htmlContainer) return;
        const br = document.createElement("br");
        htmlContainer.appendChild(br);
        const ul = document.createElement("ul");
        ul.style.listStyle = "none";
        ul.style.paddingLeft = "0px";
        ul.style.margin = "10px 0 0";
        list.map((item) => {
          const li = document.createElement("li");
          li.style.padding = "6px 0";
          li.style.borderTop = `1px solid ${INPUT_COLOR_BORDER}`;
          const textnode = document.createTextNode(item);
          li.appendChild(textnode);
          ul.appendChild(li);
          return null;
        });
        htmlContainer.appendChild(ul);
      }
      appendAdditionalText(additionalText);
      if (html) {
        enableInteractiveHtmlInSwal();
      }
    },
    willClose: (popup) => {
      unmountMuiIconFromPopup(popup);
    },
  });
};

export const TimerAlert = ({
  title = "",
  html = "",
  timer = 2000,
  timerProgressBar = true,
}) => {
  return MySwal.fire({
    title,
    html,
    timer,
    timerProgressBar,
    customClass: buildCustomClass({ withIcon: true, includeDeny: false }),
    didOpen: () => {
      didOpenBase({
        icon: "warning",
        reverseButtons: false,
        compactButtons: false,
        useMuiIcon: false,
      });

      MySwal.showLoading();
    },
    willClose: (popup) => {
      unmountMuiIconFromPopup(popup);
    },
  });
};
